'use client'

import UIFlex from '@components/ui/UIFlex'
import UIText from '@components/ui/UIText'
import { pxToRem } from '@ui/style/muiTheme'
import { Box } from '@mui/joy'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import { ElectionMetaResponseVo } from '@schemas/non-auth'

interface ElectionBannerTopProps {
	data: ElectionMetaResponseVo
}

const getElectionStatusText = (status: string) => {
	switch (status) {
		case 'beforeBallot':
			return '투표전'
		case 'ballotProgress':
			return '투표중'
		case 'ballotComplete':
			return '투표완료'
		case 'voteProgress':
			return '개표중'
		case 'voteComplete':
			return '개표완료'
		case 'resultComplete':
			return '당선결과완료'
		default:
			return '투표전'
	}
}

const getVoteRateText = (status: string) => {
	switch (status) {
		case 'ballotBanner':
		case 'predictiveBanner':
			return '투표율'
		case 'voteBanner':
			return '개표율'
		default:
			return '투표율'
	}
}

const getVoteLocationText = (type: string, isError: boolean, name: string) =>
	isError ? `전국 ${getVoteRateText(type)}` : `${name} ${getVoteRateText(type)}`

const getReferenceTimeText = (isError: boolean, referenceTime: string) =>
	isError ? '오전 00시 00분' : `(${referenceTime} 기준)`

const getVoteValue = (isError: boolean, value: string | number) => (isError ? '00.00' : value)

const ElectionBannerTop = ({ data }: ElectionBannerTopProps) => {
	const {
		isError: isErrorBallot,
		rollingData: rollingDataBallot,
		referenceTime: referenceTimeBallot,
	} = data?.topBannerChart?.ballotChart || { isError: true, rollingData: [], referenceTime: '' }

	const {
		isError: isErrorVote,
		rollingData: rollingDataVote,
		referenceTime: referenceTimeVote,
	} = data?.topBannerChart?.voteChart || { isError: true, rollingData: [], referenceTime: '' }

	const isBallotOrPredictive =
		data.topBannerType === 'ballotBanner' || data.topBannerType === 'predictiveBanner'

	const isError = isBallotOrPredictive ? isErrorBallot : isErrorVote
	const rollingData = isBallotOrPredictive ? rollingDataBallot : rollingDataVote
	const referenceTime = isBallotOrPredictive ? referenceTimeBallot : referenceTimeVote

	return (
		<UIFlex
			flexDirection="row"
			justifyContent="space-between"
			height="100%"
			sx={{
				padding: {
					xs: `0 ${pxToRem(20)}`,
					md: 0,
				},
			}}
		>
			<UIFlex flexDirection="column" gap={{ xs: pxToRem(8), md: pxToRem(12) }}>
				<UIText
					fontWeight={700}
					color="#082B4F"
					sx={{
						fontSize: {
							xs: pxToRem(14),
							lg: pxToRem(18),
						},
						lineHeight: {
							xs: pxToRem(14),
							lg: pxToRem(18),
						},
					}}
				>
					제 21대 대통령 선거
				</UIText>
				<Box
					sx={{
						backgroundImage: 'url(/images/election/election_banner_title_pc.png)',
						width: {
							xs: pxToRem(169),
							lg: pxToRem(237),
						},
						height: {
							xs: pxToRem(20),
							lg: pxToRem(28),
						},
						backgroundSize: 'contain',
						backgroundPosition: 'center',
						backgroundRepeat: 'no-repeat',
						textIndent: '-9999rem',
					}}
				>
					2025 우리의 선택
				</Box>
			</UIFlex>
			<UIFlex flexDirection="row" gap={pxToRem(8)} alignItems="center">
				<UIFlex
					sx={{
						justifyContent: 'center',
						alignItems: 'center',
						backgroundColor: '#082B4F',
						padding: {
							xs: `${pxToRem(8)} ${pxToRem(12)}`,
							md: `${pxToRem(8)} ${pxToRem(14)}`,
						},
						borderRadius: pxToRem(100),
					}}
				>
					<UIText
						sx={{
							color: '#fff',
							fontWeight: 700,
							fontSize: {
								xs: pxToRem(14),
								lg: pxToRem(16),
							},
							lineHeight: {
								xs: pxToRem(14),
								lg: pxToRem(16),
							},
						}}
					>
						{getElectionStatusText(data.electionStatus)}
					</UIText>
				</UIFlex>
				<Box
					sx={{
						height: {
							xs: pxToRem(40),
							md: pxToRem(30),
							lg: pxToRem(32),
						},
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<Swiper
						direction="vertical"
						autoplay={{
							delay: 5000,
							disableOnInteraction: false,
						}}
						modules={[Autoplay]}
						spaceBetween={8}
						loop
						allowTouchMove={false}
						style={{ height: '100%' }}
					>
						{rollingData.map((item) => (
							<SwiperSlide key={item.name}>
								<UIFlex
									flexDirection={{
										xs: 'column',
										md: 'row',
									}}
									alignItems={{
										xs: 'flex-start',
										md: 'center',
									}}
									gap={{
										xs: pxToRem(2),
										md: pxToRem(8),
									}}
								>
									<UIFlex flexDirection="column" gap={pxToRem(4)}>
										<UIText
											sx={{
												position: 'relative',
												top: pxToRem(1),
												color: '#082B4F',
												fontWeight: 700,
												fontSize: {
													xs: pxToRem(13),
													lg: pxToRem(15),
												},
												lineHeight: {
													xs: pxToRem(13),
													lg: pxToRem(15),
												},
											}}
										>
											<UIText
												sx={{
													color: '#082B4F',
													display: {
														xs: 'none',
														md: 'inline-block',
													},
												}}
											>
												실시간&nbsp;
											</UIText>
											{getVoteLocationText(
												data.topBannerType,
												isError,
												item.name,
											)}
										</UIText>
										<UIText
											sx={{
												color: '#082B4F',
												opacity: 0.7,
												fontSize: pxToRem(10),
												lineHeight: pxToRem(10),
												display: {
													xs: 'none',
													md: 'block',
												},
											}}
										>
											{getReferenceTimeText(isError, referenceTime)}
										</UIText>
									</UIFlex>
									<UIText
										sx={{
											position: 'relative',
											color: '#082B4F',
											fontWeight: 700,
											fontSize: {
												xs: pxToRem(24),
												lg: pxToRem(32),
											},
											lineHeight: {
												xs: pxToRem(24),
												lg: pxToRem(32),
											},
										}}
									>
										{getVoteValue(isError, item.value)}
										<UIText
											sx={{
												fontSize: {
													xs: pxToRem(12),
													lg: pxToRem(16),
												},
												lineHeight: {
													xs: pxToRem(16),
													lg: pxToRem(20),
												},
												fontFeatureSettings: 'case',
												marginLeft: pxToRem(2),
												color: '#082B4F',
											}}
										>
											%
										</UIText>
									</UIText>
								</UIFlex>
							</SwiperSlide>
						))}
					</Swiper>
				</Box>
			</UIFlex>
		</UIFlex>
	)
}

export default ElectionBannerTop
